import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { LOGO_URL } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, CheckCircle2, ArrowLeft, Loader2 } from "lucide-react";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Missing reset token. Use the link from your email."); return; }
    if (form.next !== form.confirm) { toast.error("Passwords do not match."); return; }
    if (form.next.length < 10) { toast.error("Password must be at least 10 characters."); return; }
    if (/\s/.test(form.next)) { toast.error("Password cannot contain spaces."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.next });
      setDone(true);
      toast.success("Password updated.");
      setTimeout(() => nav("/admin/login"), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#070707] flex items-center justify-center p-6" data-testid="admin-reset-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Elite Pixels" className="h-16 mx-auto" />
          <div className="text-[0.55rem] tracking-[0.4em] uppercase text-white/40 mt-3">Admin · Choose a New Password</div>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 p-8">
          {!token ? (
            <div className="text-center">
              <h2 className="font-serif text-2xl mb-3">Reset link missing</h2>
              <p className="text-sm text-white/60">
                It looks like you opened this page without a reset token. Click the link in your email, or request a new one.
              </p>
              <Link to="/admin/forgot-password" className="btn-ghost mt-6 inline-flex">Request a new link</Link>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-done">
              <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 size={22} className="text-[#D4AF37]"/>
              </div>
              <h2 className="font-serif text-2xl mb-3">Password updated</h2>
              <p className="text-sm text-white/60">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-2xl mb-2">Choose a new password</h1>
              <p className="text-sm text-white/55 mb-6 leading-relaxed">
                At least 10 characters, no spaces. After saving you'll be redirected to sign in.
              </p>
              <form onSubmit={submit} className="space-y-4">
                {["next","confirm"].map((k) => (
                  <label key={k} className="block">
                    <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">
                      {k === "next" ? "New password" : "Confirm new password"}
                    </span>
                    <div className="relative mt-1">
                      <input type={show ? "text" : "password"} required minLength={10}
                        value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })}
                        autoComplete="new-password"
                        data-testid={`reset-${k}`}
                        className="w-full bg-black border border-white/15 px-3 py-2.5 pr-10 text-sm focus:border-[#D4AF37] outline-none" />
                      {k === "next" && (
                        <button type="button" onClick={() => setShow(s => !s)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#D4AF37] p-1">
                          {show ? <EyeOff size={14}/> : <Eye size={14}/>}
                        </button>
                      )}
                    </div>
                  </label>
                ))}
                <button type="submit" disabled={busy} className="btn-gold w-full justify-center" data-testid="reset-submit">
                  {busy ? <><Loader2 size={14} className="animate-spin"/> Saving…</> : <><Lock size={14}/> Update Password</>}
                </button>
              </form>
            </>
          )}
          <Link to="/admin/login" className="mt-6 flex items-center justify-center gap-2 text-xs tracking-widest uppercase text-white/40 hover:text-[#D4AF37] transition">
            <ArrowLeft size={12}/> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
